import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  PlayCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  TeamOutlined,
  CalculatorOutlined,
  UploadOutlined,
  DownloadOutlined,
  ImportOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import {
  Button,
  Select,
  Table,
  Modal,
  DatePicker,
  Tabs,
  message,
  Card,
  Space,
  Popconfirm,
  Upload,
  InputNumber,
} from 'antd';
import dayjs from 'dayjs';
import { ContentBackground } from '@/components/content';
import {
  ContentWrapper,
  ContentHeader,
  ContentBody,
} from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { ROLES_PERMISSIONS } from '@/constants';
import { useCompaniesDropdown } from '@/hooks/queries/use-companies-dropdown';
import * as ozService from '@/services/payrollEngine.service';
import OzCompanyConfig from './OzCompanyConfig';
import OzEmployeeProfiles from './OzEmployeeProfiles';
import OzAbSoftImport from './OzAbSoftImport';
import OzDiagnostics from './OzDiagnostics';
import MatchRateBadge from './_shared/components/MatchRateBadge';
import RunStatusTag from './_shared/components/RunStatusTag';
import ShadowDetailedReport from './_shared/components/ShadowDetailedReport';
import {
  amountColumns,
  pickAmounts,
  sumOf,
  renderAmountSummary,
} from './_shared/components/payrollAmountColumns';
import {
  usePayrollRuns,
  usePayrollRun,
  useRunPayroll,
  useDeletePayrollRun,
  useUploadGroundTruth,
} from './_shared/hooks';

function OzPage() {
  const { t } = useTranslation(['tables', 'menus', 'forms']);

  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = searchParams.get('company') || null;
  const activeTab = searchParams.get('tab') || 'runs';
  // Set by the employee profile's "Edit OZ profile" link.
  const focusEmployeeId = searchParams.get('employee') || null;

  const clearFocusEmployee = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('employee');
      return prev;
    });
  }, [setSearchParams]);

  const setCompanyId = useCallback(
    (id) => {
      setSearchParams((prev) => {
        // The focused employee belongs to the previous company.
        if (prev.get('company') !== id) prev.delete('employee');
        if (id) {
          prev.set('company', id);
        } else {
          prev.delete('company');
        }
        return prev;
      });
    },
    [setSearchParams]
  );

  const setActiveTab = useCallback(
    (tab) => {
      setSearchParams((prev) => {
        prev.set('tab', tab);
        return prev;
      });
    },
    [setSearchParams]
  );
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  // EUR/RSD srednji kurs (NBS) — Andrija unosi pre svakog run-a sa 4 decimale.
  // Engine ga koristi za konverziju EUR variations (bonus/NMB/suspension) u RSD.
  const [exchangeRate, setExchangeRate] = useState(null);

  const { data: companiesData } = useCompaniesDropdown({
    queryParams: null,
  });
  const companyOptions = useMemo(
    () => companiesData?.options || [],
    [companiesData?.options]
  );

  useEffect(() => {
    if (companiesData?.isSingle && companyOptions[0] && !companyId) {
      setCompanyId(companyOptions[0].value);
    }
  }, [companiesData, companyOptions, companyId, setCompanyId]);

  const { data: runsData, isLoading: runsLoading } = usePayrollRuns(companyId);
  const { data: runDetail } = usePayrollRun(detailOpen ? selectedRunId : null);

  // Redovi tabele detalja — ostale isplate se ne mešaju sa zaradama, kao i do sada.
  const runDetailRows = useMemo(
    () =>
      (runDetail?.items || [])
        .filter((i) => i.input?.calcType !== 'OTHER_PAYMENT')
        .map((i) => ({ ...i, ...pickAmounts(i.calculation) })),
    [runDetail]
  );

  // Zbir obustava i „za isplatu" za kartice iznad tabele. Obračuni urađeni pre
  // ovih polja nemaju ih u run.summary, pa se pada na zbir po stavkama — nad
  // SVIM stavkama, da bi kartice ostale u istoj logici kao postojeće četiri
  // (one uključuju i ostale isplate).
  const detailFallback = useMemo(() => {
    const rows = (runDetail?.items || []).map((i) => pickAmounts(i.calculation));
    return {
      deductions: sumOf(rows, 'deductions'),
      netPayout: sumOf(rows, 'netPayout'),
    };
  }, [runDetail]);
  const runPayroll = useRunPayroll();
  const deleteRun = useDeletePayrollRun();
  const uploadGroundTruth = useUploadGroundTruth();

  const handleUploadGroundTruth = async (file) => {
    try {
      const result = await uploadGroundTruth.mutateAsync({
        runId: selectedRunId,
        file,
      });
      const s = result.summary?.matchRate ?? result.matchRate ?? 0;
      const c = result.components?.matchRate ?? 0;
      const p = result.prevoz?.matchRate ?? 0;
      const o = result.obustave?.matchRate ?? 0;
      const unmatchedText = result.unmatchedNames?.length
        ? ` • ${result.unmatchedNames.length} ${t('unmatched', { ns: 'forms', defaultValue: 'nemapirano' })}`
        : '';
      message.success(
        `Summary ${s}% • Komponente ${c}% • Prevoz ${p}% • Obustave ${o}%${unmatchedText}`
      );
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    }
    return false;
  };

  const [downloading, setDownloading] = useState(false);
  const handleDownloadXlsx = async () => {
    if (!selectedRunId) return;
    setDownloading(true);
    try {
      await ozService.downloadPayrollXlsx(selectedRunId);
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    } finally {
      setDownloading(false);
    }
  };

  const [payslipLang, setPayslipLang] = useState('sr');
  const [payslipLoading, setPayslipLoading] = useState(false);
  const handleDownloadPayslips = async () => {
    if (!selectedRunId) return;
    setPayslipLoading(true);
    try {
      await ozService.downloadOzPayslips(selectedRunId, payslipLang);
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    } finally {
      setPayslipLoading(false);
    }
  };

  const handleRunPayroll = async () => {
    if (!companyId || !selectedMonth) {
      message.warning(t('selectCompanyMonthYear', { ns: 'forms' }));
      return;
    }
    if (!exchangeRate || exchangeRate <= 0) {
      message.warning(
        t('exchangeRateRequired', {
          ns: 'forms',
          defaultValue: 'Unesi NBS EUR kurs pre kalkulacije',
        })
      );
      return;
    }
    try {
      const result = await runPayroll.mutateAsync({
        companyId,
        month: selectedMonth.month() + 1,
        year: selectedMonth.year(),
        type: 'SALARY',
        exchangeRate,
      });
      message.success(
        `${t('calculationComplete', { ns: 'forms' })}: ${result.summary?.calculatedEmployees || 0} ${t('employees', { ns: 'forms' }).toLowerCase()}`
      );
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    }
  };

  const handleDelete = async (runId) => {
    try {
      await deleteRun.mutateAsync(runId);
      message.success(t('deleted', { ns: 'forms' }));
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    }
  };

  const columns = [
    {
      title: t('month', { ns: 'forms' }),
      dataIndex: 'month',
      key: 'month',
    },
    {
      title: t('type', { ns: 'forms' }),
      dataIndex: 'type',
      key: 'type',
      render: (v) => {
        const map = {
          SALARY: t('ozTypeSalary', { ns: 'forms' }),
          BONUS: t('ozTypeBonus', { ns: 'forms' }),
          OTHER: t('ozTypeOther', { ns: 'forms' }),
        };
        return map[v] || v;
      },
    },
    {
      title: t('status', { ns: 'forms' }),
      dataIndex: 'status',
      key: 'status',
      render: (status) => <RunStatusTag status={status} />,
    },
    {
      title: t('employees', { ns: 'forms' }),
      key: 'employees',
      render: (_, r) =>
        `${r.summary?.calculatedEmployees || 0}/${r.summary?.totalEmployees || 0}`,
    },
    {
      title: t('ozMatch', { ns: 'forms' }),
      key: 'match',
      render: (_, r) => <MatchRateBadge rate={r.shadowComparison?.matchRate} />,
    },
    {
      title: t('gross', { ns: 'forms' }),
      key: 'gross',
      render: (_, r) => r.summary?.totalGross?.toLocaleString('sr-RS') || '-',
    },
    {
      title: t('net', { ns: 'forms' }),
      key: 'net',
      render: (_, r) => r.summary?.totalNet?.toLocaleString('sr-RS') || '-',
    },
    {
      // Obračuni urađeni pre ovih polja ih nemaju u summary — tada stoji '-'.
      title: t('ozDeductions', { ns: 'forms' }),
      key: 'deductions',
      render: (_, r) =>
        r.summary?.totalDeductions?.toLocaleString('sr-RS') || '-',
    },
    {
      title: t('ozNetPayout', { ns: 'forms' }),
      key: 'netPayout',
      render: (_, r) =>
        r.summary?.totalNetPayout?.toLocaleString('sr-RS') || '-',
    },
    {
      title: t('ozGrossTwo', { ns: 'forms' }),
      key: 'grossTwo',
      render: (_, r) =>
        r.summary?.totalEmployerCost?.toLocaleString('sr-RS') || '-',
    },
    {
      title: '',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedRunId(r._id);
              setDetailOpen(true);
            }}
          />
          <Popconfirm
            title={t('confirmDelete', { ns: 'forms' })}
            onConfirm={() => handleDelete(r._id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'runs',
      label: (
        <span>
          <CalculatorOutlined className="mr-1" />
          {t('ozRuns', { ns: 'forms' })}
        </span>
      ),
      children: (
        <>
          <div className="mb-3 flex items-center gap-2">
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={setSelectedMonth}
              allowClear={false}
              format="MMMM YYYY"
            />
            <InputNumber
              value={exchangeRate}
              onChange={setExchangeRate}
              precision={4}
              min={0}
              step={0.0001}
              placeholder={t('eurRate', {
                ns: 'forms',
                defaultValue: 'EUR kurs (NBS)',
              })}
              style={{ width: 180 }}
              addonBefore="EUR"
            />
            <Button
              className="btn-secondary whitespace-nowrap !rounded-[50px]"
              icon={<PlayCircleOutlined />}
              loading={runPayroll.isPending}
              onClick={handleRunPayroll}
              disabled={!companyId}
            >
              <strong>{t('runCalculation', { ns: 'forms' })}</strong>
            </Button>
          </div>
          <Table
            dataSource={runsData?.data || []}
            columns={columns}
            rowKey="_id"
            loading={runsLoading}
            size="small"
            pagination={false}
          />
        </>
      ),
    },
    {
      key: 'config',
      label: (
        <span>
          <SettingOutlined className="mr-1" />
          {t('ozConfig', { ns: 'forms' })}
        </span>
      ),
      children: <OzCompanyConfig companyId={companyId} />,
    },
    {
      key: 'profiles',
      label: (
        <span>
          <TeamOutlined className="mr-1" />
          {t('ozProfiles', { ns: 'forms' })}
        </span>
      ),
      children: (
        <OzEmployeeProfiles
          companyId={companyId}
          focusEmployeeId={focusEmployeeId}
          onClearFocus={clearFocusEmployee}
        />
      ),
    },
    {
      key: 'import',
      label: (
        <span>
          <ImportOutlined className="mr-1" />
          {t('ozImportAbSoft', { ns: 'forms' })}
        </span>
      ),
      children: <OzAbSoftImport companyId={companyId} />,
    },
    {
      key: 'diagnostics',
      label: (
        <span>
          <DatabaseOutlined className="mr-1" />
          {t('ozDiagnostics', { ns: 'forms' })}
        </span>
      ),
      children: <OzDiagnostics companyId={companyId} />,
    },
  ];

  return (
    <RootLayout
      metadata={{
        title: t('obracunZarada', { ns: 'menus' }),
      }}
    >
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader
              title={t('obracunZarada', {
                ns: 'menus',
              })}
            >
              <Select
                className="min-w-[280px]"
                placeholder={t('selectCompany', {
                  ns: 'forms',
                })}
                value={companyId}
                onChange={setCompanyId}
                showSearch
                allowClear
                optionFilterProp="label"
                options={companyOptions}
              />
            </ContentHeader>
            <ContentBody className="p-4">
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
              />
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>

      <Modal
        title={`${t('ozRunDetail', { ns: 'forms' })}: ${runDetail?.run?.month || ''}`}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        width="90vw"
        style={{ maxWidth: 1400, top: 24 }}
        footer={null}
      >
        {runDetail && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <Card size="small" title={t('gross', { ns: 'forms' })}>
                {runDetail.run.summary?.totalGross?.toLocaleString('sr-RS')} RSD
              </Card>
              <Card size="small" title={t('net', { ns: 'forms' })}>
                {runDetail.run.summary?.totalNet?.toLocaleString('sr-RS')} RSD
              </Card>
              <Card size="small" title={t('tax', { ns: 'forms' })}>
                {runDetail.run.summary?.totalTax?.toLocaleString('sr-RS')} RSD
              </Card>
              <Card size="small" title={t('ozDeductions', { ns: 'forms' })}>
                {(
                  runDetail.run.summary?.totalDeductions ??
                  detailFallback.deductions
                ).toLocaleString('sr-RS')}{' '}
                RSD
              </Card>
              <Card size="small" title={t('ozNetPayout', { ns: 'forms' })}>
                {(
                  runDetail.run.summary?.totalNetPayout ??
                  detailFallback.netPayout
                ).toLocaleString('sr-RS')}{' '}
                RSD
              </Card>
              <Card size="small" title={t('totalCost', { ns: 'forms' })}>
                {runDetail.run.summary?.totalEmployerCost?.toLocaleString(
                  'sr-RS'
                )}{' '}
                RSD
              </Card>
              <Upload
                accept=".xlsx"
                maxCount={1}
                showUploadList={false}
                beforeUpload={handleUploadGroundTruth}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={uploadGroundTruth.isPending}
                >
                  {t('ozUploadGroundTruth', {
                    ns: 'forms',
                    defaultValue: 'Upload AB Soft xlsx',
                  })}
                </Button>
              </Upload>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={handleDownloadXlsx}
              >
                {t('ozDownloadHlbXlsx', {
                  ns: 'forms',
                  defaultValue: 'Preuzmi HLB obračun',
                })}
              </Button>
              <Select
                value={payslipLang}
                onChange={setPayslipLang}
                style={{ width: 110 }}
                options={[
                  { value: 'sr', label: 'Srpski' },
                  { value: 'en', label: 'English' },
                  { value: 'ru', label: 'Русский' },
                ]}
              />
              <Button
                icon={<DownloadOutlined />}
                loading={payslipLoading}
                onClick={handleDownloadPayslips}
              >
                {t('ozDownloadPayslips', {
                  ns: 'forms',
                  defaultValue: 'Preuzmi obračunske listiće',
                })}
              </Button>
            </div>

            {runDetail.run.shadowComparison?.matchRate != null ? (
              <ShadowDetailedReport
                items={runDetail.items}
                layers={runDetail.run.shadowComparison.layers}
              />
            ) : (
              <Table
                dataSource={runDetailRows}
                rowKey="_id"
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                summary={() =>
                  renderAmountSummary({ t, rows: runDetailRows })
                }
                columns={[
                  {
                    title: t('employee', { ns: 'forms' }),
                    dataIndex: 'employeeName',
                    render: (v) => v || '-',
                  },
                  {
                    title: t('type', { ns: 'forms' }),
                    dataIndex: ['input', 'calcType'],
                    render: (v) => {
                      const map = {
                        TYPE_01_FIXED_RSD: t('ozCalcType01', { ns: 'forms' }),
                        TYPE_02_POINTS: t('ozCalcType02', { ns: 'forms' }),
                        TYPE_05_POINTS_INPUT: t('ozCalcType05', {
                          ns: 'forms',
                        }),
                        TYPE_07_ADVANCE: t('ozCalcType07', { ns: 'forms' }),
                        TYPE_08_POINTS_NO_HOURS: t('ozCalcType08', {
                          ns: 'forms',
                        }),
                        TYPE_11_PREV_MONTH: t('ozCalcType11', { ns: 'forms' }),
                        TYPE_40_AVG_3M: t('ozCalcType40', { ns: 'forms' }),
                        TYPE_41_AVG_CONTRACT: t('ozCalcType41', {
                          ns: 'forms',
                        }),
                        TYPE_47_MATERNITY_12M: t('ozCalcType47', {
                          ns: 'forms',
                        }),
                        TYPE_62_BASE_LIMIT: t('ozCalcType62', { ns: 'forms' }),
                      };
                      return map[v] || v || '-';
                    },
                  },
                  ...amountColumns(t),
                  {
                    title: t('status', { ns: 'forms' }),
                    dataIndex: 'status',
                    render: (s) => <RunStatusTag status={s} />,
                  },
                ]}
              />
            )}
          </>
        )}
      </Modal>
    </RootLayout>
  );
}

const OzEngine = withPrivateRoute(OzPage, ROLES_PERMISSIONS.OZ.VIEW);

export default OzEngine;
